import React, { useState, useEffect } from 'react';
import {
  Briefcase, ClockCounterClockwise, PencilSimple, ArrowRight, Info, ArrowClockwise, Check, X,
} from '@phosphor-icons/react';
import {
  getBranchNames as fetchBranchNames, getGroupCode as fetchGroupCode, getTickets as fetchTickets,
  getBusinessRefByTicket as fetchBusinessRefByTicket, updateBusinessRef, saveData as saveAuditLog,
  getEmployeeCodes as fetchEmployeeCodes,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import SectionLock from './SectionLock';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

/** dd-MM-yyyy for display; the API sends an ISO date (or null when unset). */
function toDisplayDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

/** True when the enroll date is more than two calendar months before today. */
function isOlderThanTwoMonths(value) {
  if (!value) return false;
  const enrolled = new Date(value);
  if (Number.isNaN(enrolled.getTime())) return false;
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setMonth(cutoff.getMonth() - 2);
  enrolled.setHours(0, 0, 0, 0);
  return enrolled < cutoff;
}

function BusinessChange() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchlist, setBranchlist] = useState([]);
  const [branchCodeList, setBranchCodeList] = useState([]); // groups
  const [tickets, setTickets] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [ticket, setTicket] = useState('');

  const [susbName, setSusbName] = useState('');
  const [empName, setEmpName] = useState('');
  const [enrollDate, setEnrollDate] = useState('');
  const [oldEmpCode, setOldEmpCode] = useState('');
  const [oldBranchName, setOldBranchName] = useState('');
  // Still tracked (the UPDATE matches on it) but no longer shown as a textbox.
  const [oldBsID, setOldBsID] = useState('');
  const [newBsID, setNewBsID] = useState('');
  const [reason, setReason] = useState('');
  // Set when the ticket's enroll date is over two months old; the details stay hidden.
  const [enrollExpired, setEnrollExpired] = useState(false);
  const todayDisplay = toDisplayDate(new Date());

  // New reference picker. business_reference_id IS the employee's contact_id, so
  // that is both the option value and what the save writes.
  const [employees, setEmployees] = useState([]);
  const [newEmpName, setNewEmpName] = useState('');
  const [newEmpCode, setNewEmpCode] = useState('');
  const [newBranchName, setNewBranchName] = useState('');

  const [branchschema, setBranchschema] = useState(null);
  const [, setGroupCode] = useState(null);
  const [chitgroupId, setChitgroupId] = useState(null);
  const [contactId, setContactId] = useState(null);
  const [subscriberId, setSubscriberId] = useState(null);
  const [ticketNo, setTicketNo] = useState(null);

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingUpdatePayload, setPendingUpdatePayload] = useState(null);
  const [pendingAudit, setPendingAudit] = useState(null);
  const [pendingOldBsID, setPendingOldBsID] = useState('');
  const [pendingNewBsID, setPendingNewBsID] = useState('');
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    fetchBranchNames().then((res) => setBranchlist(res || [])).catch(() => setBranchlist([]));
  }, [formKey]);

  useEffect(() => {
    fetchEmployeeCodes().then((res) => setEmployees(res || [])).catch(() => setEmployees([]));
  }, []);

  const resetFromTicket = () => {
    setEnrollExpired(false);
    setContactId(null);
    setSubscriberId(null);
    setTicketNo(null);
    setSusbName('');
    setEmpName('');
    setEnrollDate('');
    setOldEmpCode('');
    setOldBranchName('');
    setOldBsID('');
    setNewBsID('');
    setNewEmpName('');
    setNewEmpCode('');
    setNewBranchName('');
  };

  const resetFromGroup = () => {
    setGroupCode(null);
    setChitgroupId(null);
    setContactId(null);
    setSubscriberId(null);
    setTicket('');
    resetFromTicket();
    setTickets([]);
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setContactId(null);
    setSubscriberId(null);
    setGroup('');
    resetFromGroup();
    setBranchCodeList([]);
    setTickets([]);
  };

  const loadGroups = async (branch) => {
    const schema = branch.branch_code;
    setBranchschema(schema);
    try {
      const res = await fetchGroupCode(schema);
      setBranchCodeList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setBranchCodeList([]);
    }
  };

  const loadTickets = async (schema, gCode) => {
    try {
      const res = await fetchTickets(schema, gCode);
      setTickets(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTickets([]);
    }
  };

  const handleCaoChange = (value) => {
    setCao(value || '');
    resetFromBranch();
    if (!value) return;
    const selected = branchlist.find((x) => x.branch_name === value);
    if (selected) loadGroups(selected);
  };

  const handleGroupChange = (value) => {
    setGroup(value || '');
    resetFromGroup();
    if (!value) return;
    const selected = branchCodeList.find((x) => x.group_Code === value);
    if (selected) {
      setGroupCode(selected.group_Code);
      const cgId = selected.chitgroup_id != null ? Number(selected.chitgroup_id) : null;
      setChitgroupId(cgId);
      loadTickets(branchschema, selected.group_Code);
    }
  };

  const handleTicketChange = async (value) => {
    setTicket(value || '');
    resetFromTicket();
    if (!value) return;
    const selected = tickets.find((x) => x.ticketno === value);
    if (!selected) return;
    setTicketNo(selected.ticketno);
    try {
      const res = await fetchBusinessRefByTicket(branchschema, selected.ticketno, chitgroupId);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
      if (isOlderThanTwoMonths(res.allocationDate)) {
        setEnrollExpired(true);
        showToast('warn', 'Enroll Date Too Old', 'The enroll date is before two months from the current date, so the details are not shown.');
        return;
      }
      setContactId(res.contactId);
      setSubscriberId(res.subscriberId);
      setSusbName(res.subscriberName || '');
      setEmpName(res.employeeName || '');
      setEnrollDate(toDisplayDate(res.allocationDate));
      setOldEmpCode(res.employeeCode || '');
      setOldBranchName(res.branchName || '');
      setOldBsID(res.businessReferenceId || '');
    } catch (err) {
      showToast('error', 'Load Failed', apiErrorMessage(err, 'Could not load business reference details for this ticket.'));
    }
  };

  const handleNewBsIDChange = (value) => {
    setNewBsID(value ? String(value) : '');
    const picked = employees.find((x) => String(x.contact_id) === String(value));
    setNewEmpName(picked?.business_reference_name || '');
    setNewEmpCode(picked?.employee_code || '');
    setNewBranchName(picked?.branch_name || '');
  };

  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && !ticket;
  const newBsIDInvalid = touched && (!newBsID || !/^[0-9]*$/.test(newBsID));
  const reasonInvalid = touched && (reason.length < 10 || reason.length > 200);

  const isValid = !!cao && !!group && !!ticket && !!contactId && !!subscriberId
    && !!newBsID && /^[0-9]+$/.test(newBsID)
    && reason.length >= 10 && reason.length <= 200;

  const handleSave = () => {
    setTouched(true);

    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    setPendingOldBsID(oldBsID || '—');
    setPendingNewBsID(newBsID);
    setPendingReason(reason);

    setPendingUpdatePayload({
      branchschema,
      chitgroup_id: Number(chitgroupId),
      ticketno: ticketNo,
      subscriberId,
      oldBusinessReferenceId: oldBsID,
      newBusinessReferenceId: newBsID,
      // Saved as business_reference_name. Employee code and branch name come from the
      // employee master through the new id, so they need no field of their own.
      newBusinessReferenceName: newEmpName,
      ptypeofoperation: 'UPDATE',
    });

    setPendingAudit({
      schemaName: branchschema,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'BUSINESS_REF_CHANGE',
      oldData: {
        businessReferenceId: oldBsID,
        employeeName: empName,
        employeeCode: oldEmpCode,
        employeeBranchName: oldBranchName,
        enrollDate,
      },
      newData: {
        businessReferenceId: newBsID,
        employeeName: newEmpName,
        employeeCode: newEmpCode,
        employeeBranchName: newBranchName,
      },
      reason,
    });

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
    setPendingUpdatePayload(null);
    setPendingAudit(null);
  };

  const confirmSave = async () => {
    if (!pendingUpdatePayload || !pendingAudit) return;

    setSaving(true);
    try {
      await updateBusinessRef(pendingUpdatePayload, branchschema);
      try {
        await saveAuditLog([pendingAudit]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Business reference updated and audit saved successfully.', true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Record updated, but audit log failed to save.', true);
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('Update failed:', err);
      showToast('error', 'Update Failed', apiErrorMessage(err, 'Could not update the business reference. Please try again.'), true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setReason('');
    setTouched(false);
    setPendingUpdatePayload(null);
    setPendingAudit(null);
    setPendingOldBsID('');
    setPendingNewBsID('');
    setPendingReason('');
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="business-change-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="business-change-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Briefcase size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Business Reference Change</div>
          </div>
        </div>

        {/* SECTION A: Select Ticket */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group and ticket to load member details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Branch (CAO) <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleCaoChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="business-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={branchCodeList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a branch first"
              disabled={!cao}
              error={groupInvalid}
              testId="business-group-select"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket no"
              emptyMessage="Select a group first"
              disabled={!group}
              error={ticketInvalid}
              testId="business-ticket-select"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        {/* SECTION B: Old vs New comparison */}
        <SectionLock locked={!ticket || enrollExpired} hint={enrollExpired ? 'Enroll date is before two months from the current date' : 'Select a branch, group code and ticket in section A first'}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Update Business Reference</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Review current details on the left, pick the new employee code on the right</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '16px', alignItems: 'start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '8px' }}>
              <ClockCounterClockwise size={13} /> Current Details
              <span style={{ fontSize: '9px', fontWeight: '700', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>READ ONLY</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Subscriber Name</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={susbName || ''} readOnly data-testid="business-subscriber-name" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Employee Name</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={empName || ''} readOnly data-testid="business-employee-name" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Enroll Date</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={enrollDate || ''} readOnly data-testid="business-enroll-date" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Employee Code</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={oldEmpCode || ''} readOnly data-testid="business-employee-code" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Employee Branch Name</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={oldBranchName || ''} readOnly data-testid="business-employee-branch" />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: '28px', color: '#9CA3AF' }}>
            <ArrowRight size={18} />
            <span style={{ fontSize: '10px', marginTop: '2px' }}>Updates to</span>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#002FA7', marginBottom: '8px' }}>
              <PencilSimple size={13} /> New Details
              <span style={{ fontSize: '9px', fontWeight: '700', color: '#002FA7', background: '#EFF4FF', padding: '2px 6px', borderRadius: '4px' }}>EDITABLE</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Date</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={todayDisplay} readOnly data-testid="business-current-date" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>
                  New Employee Code <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <SearchableSelect
                  key={`newbsid-${formKey}`}
                  options={employees}
                  // Shown by employee code; the value stays the contact_id, which is what the save writes.
                  getLabel={(o) => String(o.employee_code ?? o.contact_id)}
                  getValue={(o) => String(o.contact_id)}
                  value={newBsID}
                  onChange={handleNewBsIDChange}
                  placeholder="Select new employee code"
                  emptyMessage="No employees found"
                  disabled={!ticket}
                  error={newBsIDInvalid}
                  testId="business-new-bsid"
                />
                {newBsIDInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>New Employee Code is required</div>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>New Employee Name</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={newEmpName || ''} readOnly data-testid="business-new-employee-name" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Employee New Branch Name</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={newBranchName || ''} readOnly data-testid="business-new-employee-branch" />
              </div>
            </div>
          </div>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        {/* SECTION C: Reason */}
        <SectionLock locked={!ticket || !newBsID} hint="Select the new employee code in section B first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Change</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Explain why this business reference change is being made..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="business-reason"
        />
        {reasonInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Minimum 10 characters required</div>}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="business-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving} data-testid="business-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="business-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Briefcase size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Business Reference Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to update the business reference ID for this subscriber. Please review before confirming.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Old Reference ID</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingOldBsID}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>New Reference ID</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{pendingNewBsID}</p>
                </div>
              </div>

              {[
                ['Employee Code', 'employeeCode'],
                ['Employee Name', 'employeeName'],
                ['Employee Branch', 'employeeBranchName'],
              ].map(([label, key]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Old {label}</p>
                    <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingAudit?.oldData?.[key] || '—'}</p>
                  </div>
                  <ArrowRight size={14} color="#9CA3AF" />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>New {label}</p>
                    <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{pendingAudit?.newData?.[key] || '—'}</p>
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="business-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="business-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BusinessChange;